import React, { createContext, RefObject, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react'

export const CHAT_HEADER_HEIGHT = 45
const INPUT_PADDING = 20
const MIN_INPUT_HEIGHT = 44
type Channel = 'participant' | 'careTeam'
type Layout = { height: number; reportHeight: (channel: Channel, height: number) => void }
const ChatComposerLayoutContext = createContext<Layout | null>(null)

export const ChatComposerLayoutProvider = ({
  synchronize,
  children,
}: {
  synchronize: boolean
  children: React.ReactNode
}) => {
  const [heights, setHeights] = useState({ participant: MIN_INPUT_HEIGHT, careTeam: MIN_INPUT_HEIGHT })
  const reportHeight = useCallback((channel: Channel, height: number) => {
    setHeights((current) => (current[channel] === height ? current : { ...current, [channel]: height }))
  }, [])
  const height = synchronize ? Math.max(heights.participant, heights.careTeam) : MIN_INPUT_HEIGHT
  const value = useMemo(() => ({ height, reportHeight }), [height, reportHeight])
  return <ChatComposerLayoutContext.Provider value={value}>{children}</ChatComposerLayoutContext.Provider>
}

export const useChatInputHeight = (channel: Channel, inputRef: RefObject<HTMLTextAreaElement>): number => {
  const layout = useContext(ChatComposerLayoutContext)
  const reportHeight = layout?.reportHeight

  useLayoutEffect(() => {
    const input = inputRef.current
    if (!input || !reportHeight) {
      return undefined
    }
    // Measure the textarea, not the synchronized wrapper, so inputs can shrink again.
    const measure = () =>
      reportHeight(channel, Math.max(MIN_INPUT_HEIGHT, Math.ceil(input.getBoundingClientRect().height + INPUT_PADDING)))
    measure()
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure)
    observer?.observe(input)
    return () => {
      observer?.disconnect()
      reportHeight(channel, MIN_INPUT_HEIGHT)
    }
  }, [channel, inputRef, reportHeight])

  return layout?.height ?? MIN_INPUT_HEIGHT
}
